// frontend/src/components/TopBar.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authLogout, getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead } from "../api";

export default function TopBar({ user, onLogout }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [latestNotification, setLatestNotification] = useState(null);
  const dropdownRef = useRef(null);
  const pollInterval = useRef(null);
  // The id of the newest notification we have already accounted for. A ref,
  // not state: the polling interval closes over this function once, so reading
  // `notifications` here would compare against a frozen snapshot and re-toast
  // the same item every 30s forever. null means "first load not done yet".
  const lastSeenId = useRef(null);

  const handleLogout = async () => {
    try {
      await authLogout();
      onLogout();
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
      onLogout();
      navigate("/login");
    }
  };

  const capitalizeFirst = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Load notifications
  const loadNotifications = async () => {
    try {
      const [notifs, countData] = await Promise.all([
        getNotifications(),
        getUnreadCount()
      ]);
      
      const notifList = Array.isArray(notifs) ? notifs : (notifs.results || []);
      const latest = notifList[0];

      if (lastSeenId.current === null) {
        // First load: adopt whatever is already there without announcing it.
        // Opening the page should not replay a backlog at you.
        lastSeenId.current = latest ? latest.id : 0;
      } else if (latest && latest.id > lastSeenId.current) {
        // Mark it seen *before* showing, so the next poll cannot toast it
        // again. This is what used to fail: the bell list is capped at 30
        // server-side, so for anyone with a full list `notifications.length`
        // never changed, the poll effect never re-ran, and its interval kept
        // comparing against the list from first page load.
        lastSeenId.current = latest.id;
        if (!latest.is_read) {
          setLatestNotification(latest);
          setShowToast(true);
          setTimeout(() => setShowToast(false), 5000);
        }
      }

      setNotifications(notifList);
      setUnreadCount(countData.count || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (user) {
      loadNotifications(); // Initial load
      
      pollInterval.current = setInterval(() => {
        loadNotifications();
      }, 30000); // Poll every 30 seconds
      
      return () => {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };
    }
    // Deps are `user` alone on purpose. Keying this on `notifications.length`
    // rebuilt the interval on every length change -- and, worse, stopped
    // rebuilding it once the list hit its 30-item cap, stranding the interval
    // on a stale closure. Everything the poll needs across renders is in refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
      await loadNotifications();
    }
    
    // Navigate to the time page for time-related notifications
    if (
      (notification.notification_type === "TIME_ENTRY" ||
        notification.notification_type === "TIME_APPROVAL") &&
      notification.related_id
    ) {
      setShowDropdown(false);
      navigate("/time");
    } else if (notification.notification_type === "FEEDBACK" && notification.related_id) {
      setShowDropdown(false);
      navigate(`/improvements/${notification.related_id}`);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    await loadNotifications();
  };

  const showBell = Boolean(user);

  return (
    <>
      <div style={{
        background: "#1f2937",
        color: "white",
        padding: "1rem 2rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        minWidth: "100%",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <img 
            src="/cz-logo.png" 
            alt="Client Zone Logo" 
            style={{
              height: "40px",
              width: "auto"
            }}
          />
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span style={{ fontSize: "0.875rem", color: "#d1d5db" }}>
            Welcome, {capitalizeFirst(user?.username || user?.first_name || "User")}
          </span>
          
          {/* Notification Bell */}
          {showBell && (
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  padding: "0.5rem",
                  position: "relative",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {/* Bell Icon */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                
                {/* Red Badge */}
                {unreadCount > 0 && (
                  <span style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    background: "#dc2626",
                    color: "white",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    fontSize: 11,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {showDropdown && (
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: "white",
                  borderRadius: 8,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                  width: 360,
                  maxHeight: 480,
                  overflow: "hidden",
                  zIndex: 2000
                }}>
                  <div style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: "#111827" }}>Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#2563eb",
                          fontSize: 13,
                          cursor: "pointer",
                          fontWeight: 600
                        }}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  
                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    {notifications.length === 0 ? (
                      <div style={{
                        padding: 40,
                        textAlign: "center",
                        color: "#6b7280",
                        fontSize: 14
                      }}>
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          style={{
                            padding: "12px 16px",
                            borderBottom: "1px solid #f3f4f6",
                            cursor: "pointer",
                            background: notif.is_read ? "white" : "#eff6ff",
                            transition: "background 0.2s"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                          onMouseLeave={(e) => e.currentTarget.style.background = notif.is_read ? "white" : "#eff6ff"}
                        >
                          <div style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8
                          }}>
                            {!notif.is_read && (
                              <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#dc2626",
                                marginTop: 6,
                                flexShrink: 0
                              }}></div>
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: "#111827",
                                marginBottom: 4
                              }}>
                                {notif.title}
                              </div>
                              <div style={{
                                fontSize: 13,
                                color: "#6b7280",
                                marginBottom: 4
                              }}>
                                {notif.message}
                              </div>
                              <div style={{
                                fontSize: 12,
                                color: "#9ca3af"
                              }}>
                                {new Date(notif.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <button
            onClick={handleLogout}
            style={{
              background: "#dc2626",
              color: "white",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
            onMouseOver={(e) => e.target.style.background = "#b91c1c"}
            onMouseOut={(e) => e.target.style.background = "#dc2626"}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Spacer to prevent content from hiding under fixed TopBar */}
      <div style={{ height: "64px" }} />

      {/* Toast Notification */}
      {showToast && latestNotification && (
        <div style={{
          position: "fixed",
          top: 80,
          right: 20,
          background: "white",
          borderRadius: 8,
          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
          padding: 16,
          width: 320,
          zIndex: 3000,
          animation: "slideIn 0.3s ease-out",
          border: "2px solid #2563eb"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 8
          }}>
            <div style={{
              fontWeight: 700,
              fontSize: 15,
              color: "#111827"
            }}>
              🔔 {latestNotification.title}
            </div>
            <button
              onClick={() => setShowToast(false)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                color: "#6b7280"
              }}
            >
              ✕
            </button>
          </div>
          <div style={{
            fontSize: 14,
            color: "#6b7280"
          }}>
            {latestNotification.message}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}